import React from 'react';
import { connect } from 'react-redux';
import axios from 'axios';

import { FB_URL } from '../store/actions/types';
import firebase from '../store/firebase';

import { withStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableBody from '@material-ui/core/TableBody';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import Paper from '@material-ui/core/Paper';
import Chip from '@material-ui/core/Chip';
import IconButton from '@material-ui/core/IconButton';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import Snackbar from '@material-ui/core/Snackbar';
import CloseIcon from '@material-ui/icons/Close';

// Web Sources admin screen: the allowlist of sites agents may source test content from.
// Talks only to m189-webSourceAdmin, which re-validates everything server-side; the
// validation here is for fast feedback, never for security.
// Same structure as SafeguardingDashboard: class component, withStyles (makeStyles is a hook).

const ENDPOINT = `${String(FB_URL).replace(/\/+$/, '')}/m189-webSourceAdmin`;

// Sites that mostly publish PDFs: searchable, but their PDFs cannot be used yet.
const PDF_HEAVY = ['arxiv.org', 'plos.org'];

const DOMAIN_RE = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/;

/** Mirrors the server rule (bare ASCII domain). Returns { domain } or { error }. */
function validateDomainInput(raw, existing) {
  const s = (raw || '').trim();
  if (!s) return { error: null, domain: null };
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(s)) return { error: 'Enter only the domain (edutopia.org), not a URL with https://.' };
  if (s.includes('*')) return { error: 'No wildcards. Subdomains are included automatically.' };
  if (/[/?#@:\s]/.test(s)) return { error: 'Enter only the domain, with no path, port or spaces.' };
  // eslint-disable-next-line no-control-regex
  if (/[^\x00-\x7F]/.test(s)) return { error: 'ASCII only. Use the punycode (xn--) form for international domains.' };
  const d = s.toLowerCase();
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(d)) return { error: 'IP addresses are not allowed.' };
  if (!DOMAIN_RE.test(d)) return { error: 'Not a valid domain. It needs at least one dot, for example example.org.' };
  if (existing.includes(d)) return { error: `${d} is already on the list.` };
  return { error: null, domain: d };
}

const ACTION_LABELS = {
  add: 'Added',
  remove: 'Removed',
  materializeDefaults: 'Saved defaults as editable list',
  restoreDefaults: 'Restored defaults',
  unblock: 'Unblocked',
};

const TEST_STATUS = {
  ok: { label: 'Readable', color: 'primary' },
  too_short: { label: 'Too short', color: 'default' },
  blocked: { label: 'Blocked', color: 'secondary' },
  not_html: { label: 'Not a web page', color: 'default' },
};

const styles = (theme) => ({
  root: {
    padding: theme.spacing(3),
    maxWidth: 1100,
    [theme.breakpoints.down('xs')]: { padding: theme.spacing(1) },
  },
  title: { fontWeight: 700 },
  subtitle: { color: theme.palette.text.secondary, marginBottom: theme.spacing(2) },
  banner: {
    borderLeft: `4px solid ${theme.palette.warning.main}`,
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(3),
  },
  section: { padding: theme.spacing(2), marginBottom: theme.spacing(3) },
  sectionHead: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  sectionTitle: { fontWeight: 600 },
  muted: { color: theme.palette.text.secondary },
  row: { display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: theme.spacing(2) },
  grow: { flex: '1 1 260px', minWidth: 0 },
  inlineButton: { marginTop: theme.spacing(1.5), whiteSpace: 'nowrap' },
  tableWrap: { width: '100%', overflowX: 'auto', maxHeight: 520, overflowY: 'auto' },
  denseCell: {
    padding: '4px 12px',
    lineHeight: 1.3,
    [theme.breakpoints.down('xs')]: { padding: '6px 6px' },
  },
  domainCell: { fontFamily: 'monospace', fontSize: 14, wordBreak: 'break-all' },
  chip: { marginRight: theme.spacing(0.5), marginBottom: 2 },
  hideOnMobile: { [theme.breakpoints.down('xs')]: { display: 'none' } },
  testResult: {
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(1, 1.5),
    backgroundColor: theme.palette.action.hover,
    borderRadius: 4,
    wordBreak: 'break-word',
  },
  blockedGrid: { display: 'flex', flexWrap: 'wrap', gap: theme.spacing(2) },
  blockedCol: { flex: '1 1 300px', minWidth: 0 },
  blockedItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(0.5, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  danger: {
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.error.main}`,
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
  },
  dangerButton: {
    color: theme.palette.error.main,
    borderColor: theme.palette.error.main,
  },
  loadingWrap: { display: 'flex', justifyContent: 'center', padding: theme.spacing(6) },
  errorText: { color: theme.palette.error.main },
});

class WebSourceSettings extends React.Component {
  state = {
    loading: true,
    loadError: null,
    data: null,   // { source, domains, crawlerBlocked, fetchBlocked, updatedAt, effective }
    audit: [],
    busy: null,   // name of the action in flight
    filter: '',
    newDomain: '',
    testUrl: '',
    testResult: null,
    removeDialog: { open: false, domain: null },
    restoreDialog: false,
    snack: null,
  };

  componentDidMount() {
    this.checkAccessAndLoad();
  }

  componentDidUpdate(prevProps) {
   if (prevProps.user !== this.props.user && this.state.loading && !this.state.data) {
      this.checkAccessAndLoad();
    }
  }

  checkAccessAndLoad() {
    const { user } = this.props;
   console.log('WebSource access check:', this.props.user && this.props.user.webSourceAdmin, typeof (this.props.user && this.props.user.webSourceAdmin), this.props.user);

    if (!user) return; // user record not loaded yet; componentDidUpdate retries
    if (user.webSourceAdmin !== true) {
      this.props.history.push('/');
      return;
    }
    this.load();
  }

  getIdToken = async () => {
    const currentUser = firebase.auth.currentUser; // wrapper: .auth is already an instance
    if (!currentUser) return null;
    return currentUser.getIdToken();
  };

  callAdmin = async (params) => {
    const token = await this.getIdToken();
    return axios.post(ENDPOINT, { params }, { headers: { Authorization: `Bearer ${token}` } });
  };

  errorMessage = (err, fallback) =>
    (err && err.response && err.response.data && err.response.data.error) || fallback;

  load = async () => {
    this.setState({ loading: true, loadError: null });
    try {
      const res = await this.callAdmin({ action: 'list' });
      this.setState({ data: res.data.state, audit: res.data.audit || [], loading: false });
    } catch (err) {
      this.setState({ loading: false, loadError: this.errorMessage(err, 'Failed to load web sources.') });
    }
  };

  /** Runs a mutation; the server answers with the fresh state, so no reload is needed. */
  mutate = async (busy, params, successText) => {
    this.setState({ busy });
    try {
      const res = await this.callAdmin(params);
      this.setState({
        data: res.data.state,
        audit: res.data.audit || [],
        busy: null,
        snack: res.data.auditWarning || successText,
      });
      return true;
    } catch (err) {
      this.setState({ busy: null, snack: this.errorMessage(err, 'That change failed. Please try again.') });
      return false;
    }
  };

  handleAdd = async () => {
    const { data, newDomain } = this.state;
    const v = validateDomainInput(newDomain, data.domains);
    if (!v.domain) return;
    const ok = await this.mutate('add', { action: 'add', domain: v.domain }, `${v.domain} added.`);
    if (ok) this.setState({ newDomain: '', testUrl: '', testResult: null });
  };

  handleTest = async () => {
    const { data, newDomain, testUrl } = this.state;
    const v = validateDomainInput(newDomain, data.domains);
    this.setState({ busy: 'test', testResult: null });
    try {
      const params = { action: 'testSource', url: testUrl.trim() };
      if (v.domain) params.domain = v.domain;
      const res = await this.callAdmin(params);
      this.setState({ busy: null, testResult: res.data.result });
    } catch (err) {
      this.setState({ busy: null, testResult: { status: 'error', detail: this.errorMessage(err, 'The test could not run.') } });
    }
  };

  confirmRemove = async () => {
    const { domain } = this.state.removeDialog;
    this.setState({ removeDialog: { open: false, domain: null } });
    await this.mutate('remove:' + domain, { action: 'remove', domain }, `${domain} removed.`);
  };

  confirmRestore = async () => {
    this.setState({ restoreDialog: false });
    await this.mutate('restore', { action: 'restoreDefaults', confirm: true }, 'Built-in defaults restored.');
  };

  // ── Rendering ─────────────────────────────────────────────────────────────

  renderBanner() {
    const { classes } = this.props;
    return (
      <Paper className={classes.banner} elevation={0} variant="outlined">
        <Typography variant="body2">
          <strong>Changes can take up to 5 minutes to reach all agents.</strong>
        </Typography>
        <Typography variant="body2">
          These sites are used by every agent, including for pupils from Grade 8. Choose conservatively.
        </Typography>
      </Paper>
    );
  }

  renderStatusChips(domain) {
    const { classes } = this.props;
    const { crawlerBlocked, fetchBlocked } = this.state.data;
    const chips = [];
    if (crawlerBlocked.includes(domain)) {
      chips.push(<Chip key="c" size="small" color="secondary" label="blocked for search" className={classes.chip} />);
    }
    if (fetchBlocked.includes(domain)) {
      chips.push(<Chip key="f" size="small" color="secondary" label="blocked for fetch" className={classes.chip} />);
    }
    if (PDF_HEAVY.includes(domain)) {
      chips.push(<Chip key="p" size="small" variant="outlined" label="mostly PDFs" className={classes.chip} />);
    }
    return chips.length ? chips : <span className={classes.muted}>usable</span>;
  }

  renderAllowed() {
    const { classes } = this.props;
    const { data, filter, busy } = this.state;
    const q = filter.trim().toLowerCase();
    const shown = q ? data.domains.filter((d) => d.includes(q)) : data.domains;
    const isDefaults = data.source === 'defaults';

    return (
      <Paper className={classes.section}>
        <div className={classes.sectionHead}>
          <div>
            <Typography variant="h6" className={classes.sectionTitle}>Allowed sites</Typography>
            <Typography variant="body2" className={classes.muted}>
              {isDefaults
                ? `Using the ${data.domains.length} built-in defaults.`
                : `Custom list${data.updatedAt ? `, last changed ${new Date(data.updatedAt).toLocaleString()}` : ''}.`}
              {' '}{data.domains.length} sites, {data.effective.length} usable by agents.
            </Typography>
          </div>
          {isDefaults && (
            <Button
              variant="outlined"
              color="primary"
              disabled={!!busy}
              onClick={() => this.mutate('materialize', { action: 'materializeDefaults' }, 'Defaults saved as an editable list.')}
            >
              Save defaults as an editable list
            </Button>
          )}
        </div>

        {this.renderAddSite()}

        <TextField
          size="small"
          placeholder="Filter sites"
          value={filter}
          onChange={(e) => this.setState({ filter: e.target.value })}
          style={{ margin: '16px 0 8px' }}
        />
        <div className={classes.tableWrap}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell className={classes.denseCell}>Domain</TableCell>
                <TableCell className={classes.denseCell}>Status</TableCell>
                <TableCell className={classes.denseCell} align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.map((d) => (
                <TableRow key={d} hover>
                  <TableCell className={`${classes.denseCell} ${classes.domainCell}`}>{d}</TableCell>
                  <TableCell className={classes.denseCell}>{this.renderStatusChips(d)}</TableCell>
                  <TableCell className={classes.denseCell} align="right">
                    <Button
                      size="small"
                      disabled={!!busy || data.domains.length <= 1}
                      onClick={() => this.setState({ removeDialog: { open: true, domain: d } })}
                    >
                      {busy === 'remove:' + d ? <CircularProgress size={16} /> : 'Remove'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {shown.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} className={classes.denseCell}>No sites match “{filter}”.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <Typography variant="caption" className={classes.muted} component="p" style={{ marginTop: 8 }}>
          PDFs are not supported yet, so sites that mostly publish PDFs (arxiv.org, plos.org) can be searched but their documents cannot be used.
        </Typography>
      </Paper>
    );
  }

  renderAddSite() {
    const { classes } = this.props;
    const { data, newDomain, testUrl, testResult, busy } = this.state;
    const v = validateDomainInput(newDomain, data.domains);
    const wwwTip = v.domain && v.domain.startsWith('www.')
      ? `Tip: ${v.domain.slice(4)} would cover www and every other subdomain; ${v.domain} covers only www.`
      : null;
    const helper = v.error || wwwTip || 'Bare domain only, e.g. edutopia.org. No https://, paths or wildcards. Subdomains are included automatically.';
    const testStyle = testResult && TEST_STATUS[testResult.status];

    return (
      <Paper variant="outlined" style={{ padding: 16 }}>
        <Typography variant="subtitle1" className={classes.sectionTitle}>Add a site</Typography>
        <div className={classes.row}>
          <TextField
            className={classes.grow}
            label="Domain"
            value={newDomain}
            error={!!v.error}
            helperText={helper}
            onChange={(e) => this.setState({ newDomain: e.target.value, testResult: null })}
            onKeyDown={(e) => { if (e.key === 'Enter' && v.domain) this.handleAdd(); }}
            inputProps={{ autoCapitalize: 'none', autoCorrect: 'off', spellCheck: false }}
          />
          <Button
            className={classes.inlineButton}
            variant="contained"
            color="primary"
            disabled={!v.domain || !!busy}
            onClick={this.handleAdd}
          >
            {busy === 'add' ? <CircularProgress size={20} /> : 'Add'}
          </Button>
        </div>
        <div className={classes.row} style={{ marginTop: 8 }}>
          <TextField
            className={classes.grow}
            label="Optional: test an article URL from this site"
            placeholder="https://..."
            value={testUrl}
            helperText="Checks that agents can actually open and read this site's articles. Nothing is saved."
            onChange={(e) => this.setState({ testUrl: e.target.value, testResult: null })}
            inputProps={{ autoCapitalize: 'none', autoCorrect: 'off', spellCheck: false }}
          />
          <Button
            className={classes.inlineButton}
            variant="outlined"
            disabled={!/^https:\/\/\S+$/i.test(testUrl.trim()) || !!busy}
            onClick={this.handleTest}
          >
            {busy === 'test' ? <CircularProgress size={20} /> : 'Test'}
          </Button>
        </div>
        {testResult && (
          <div className={classes.testResult}>
            <Chip
              size="small"
              className={classes.chip}
              color={testStyle ? testStyle.color : 'default'}
              label={testStyle ? testStyle.label : testResult.status === 'error' ? 'Error' : testResult.status}
            />
            <Typography variant="body2" component="span">{testResult.detail}</Typography>
            {testResult.title && (
              <Typography variant="caption" component="p" className={classes.muted}>
                “{testResult.title}”
              </Typography>
            )}
          </div>
        )}
      </Paper>
    );
  }

  renderBlockedList(list, title, why, domains) {
    const { classes } = this.props;
    const { busy } = this.state;
    return (
      <div className={classes.blockedCol}>
        <Typography variant="subtitle1" className={classes.sectionTitle}>{title}</Typography>
        <Typography variant="body2" className={classes.muted} style={{ marginBottom: 8 }}>{why}</Typography>
        {domains.length === 0 && <Typography variant="body2">None.</Typography>}
        {domains.map((d) => (
          <div key={d} className={classes.blockedItem}>
            <span className={classes.domainCell}>{d}</span>
            <Button
              size="small"
              disabled={!!busy}
              onClick={() => this.mutate(`unblock:${list}:${d}`, { action: 'unblock', list, domain: d }, `${d} unblocked. Agents will retry it within 5 minutes.`)}
            >
              {busy === `unblock:${list}:${d}` ? <CircularProgress size={16} /> : 'Unblock'}
            </Button>
          </div>
        ))}
      </div>
    );
  }

  renderBlocked() {
    const { classes } = this.props;
    const { crawlerBlocked, fetchBlocked } = this.state.data;
    return (
      <Paper className={classes.section}>
        <Typography variant="h6" className={classes.sectionTitle} gutterBottom>Blocked by the system</Typography>
        <div className={classes.blockedGrid}>
          {this.renderBlockedList(
            'crawler',
            'Search crawler',
            "The publisher refuses Anthropic's search crawler. One such site makes a whole search fail, so the system drops it automatically.",
            crawlerBlocked
          )}
          {this.renderBlockedList(
            'fetch',
            'Our article fetch',
            'The publisher refused to let us open a page (HTTP 401/403). Added automatically after the first refusal.',
            fetchBlocked
          )}
        </div>
        <Typography variant="caption" className={classes.muted} component="p" style={{ marginTop: 8 }}>
          Unblocking gives a site another try. If the publisher still refuses, the system blocks it again on the next failure.
        </Typography>
      </Paper>
    );
  }

  describeAudit(e) {
    const label = ACTION_LABELS[e.action] || e.action;
    const count = (arr) => (Array.isArray(arr) ? arr.length : null);
    if (e.action === 'unblock') return `${label} ${e.domain} (${e.list === 'crawler' ? 'search crawler' : 'fetch'})`;
    const b = count(e.before);
    const a = count(e.after);
    const sizes = a !== null ? ` (${b !== null ? b : 'defaults'} → ${a})` : '';
    return `${label}${e.domain ? ' ' + e.domain : ''}${sizes}`;
  }

  renderAudit() {
    const { classes } = this.props;
    const { audit } = this.state;
    return (
      <Paper className={classes.section}>
        <Typography variant="h6" className={classes.sectionTitle} gutterBottom>Recent changes</Typography>
        {audit.length === 0 ? (
          <Typography variant="body2" className={classes.muted}>No changes recorded yet.</Typography>
        ) : (
          <div className={classes.tableWrap}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.denseCell}>When</TableCell>
                  <TableCell className={`${classes.denseCell} ${classes.hideOnMobile}`}>Who</TableCell>
                  <TableCell className={classes.denseCell}>Change</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {audit.map((e) => (
                  <TableRow key={e.id}>
                    <TableCell className={classes.denseCell} style={{ whiteSpace: 'nowrap' }}>
                      {new Date(e.at).toLocaleString()}
                    </TableCell>
                    <TableCell className={`${classes.denseCell} ${classes.hideOnMobile}`}>{e.byName || e.byUid}</TableCell>
                    <TableCell className={classes.denseCell}>{this.describeAudit(e)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Paper>
    );
  }

  renderDangerZone() {
    const { classes } = this.props;
    const { busy, data } = this.state;
    return (
      <Paper className={classes.danger} elevation={0}>
        <div className={classes.grow}>
          <Typography variant="subtitle1" className={classes.sectionTitle}>Restore defaults</Typography>
          <Typography variant="body2" className={classes.muted}>
            Replaces the current list with the built-in defaults. Sites added by hand are removed.
          </Typography>
        </div>
        <Button
          variant="outlined"
          className={classes.dangerButton}
          disabled={!!busy || data.source === 'defaults'}
          onClick={() => this.setState({ restoreDialog: true })}
        >
          {busy === 'restore' ? <CircularProgress size={20} /> : 'Restore defaults'}
        </Button>
      </Paper>
    );
  }

  renderDialogs() {
    const { removeDialog, restoreDialog, data } = this.state;
    const added = data ? data.domains.length : 0;
    return (
      <>
        <Dialog open={removeDialog.open} onClose={() => this.setState({ removeDialog: { open: false, domain: null } })}>
          <DialogTitle>Remove {removeDialog.domain}?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Agents will stop offering articles from this site within 5 minutes.
              {data && data.source === 'defaults' && ' This also turns the built-in defaults into an editable custom list.'}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => this.setState({ removeDialog: { open: false, domain: null } })}>Cancel</Button>
            <Button color="secondary" onClick={this.confirmRemove}>Remove</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={restoreDialog} onClose={() => this.setState({ restoreDialog: false })}>
          <DialogTitle>Restore the built-in defaults?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              The current list of {added} sites will be replaced by the built-in defaults.
              Sites you added will be removed; sites you removed will come back. This is recorded in Recent changes.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => this.setState({ restoreDialog: false })}>Cancel</Button>
            <Button color="secondary" onClick={this.confirmRestore}>Restore defaults</Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  render() {
    const { classes } = this.props;
    const { loading, loadError, data, snack } = this.state;

    return (
      <div className={classes.root}>
        <Typography variant="h4" className={classes.title}>Web sources</Typography>
        <Typography variant="body1" className={classes.subtitle}>
          Sites agents may search and read when building tests from internet articles
        </Typography>

        {this.renderBanner()}

        {loading && <div className={classes.loadingWrap}><CircularProgress /></div>}
        {!loading && loadError && (
          <Typography className={classes.errorText}>
            {loadError} <Button size="small" onClick={this.load}>Retry</Button>
          </Typography>
        )}
        {!loading && data && (
          <>
            {this.renderAllowed()}
            {this.renderBlocked()}
            {this.renderAudit()}
            {this.renderDangerZone()}
            {this.renderDialogs()}
          </>
        )}

        <Snackbar
          open={!!snack}
          autoHideDuration={5000}
          onClose={() => this.setState({ snack: null })}
          message={snack || ''}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          action={
            <IconButton size="small" color="inherit" onClick={() => this.setState({ snack: null })}>
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        />
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  user: (state.userDialog && state.userDialog.userDetails && state.userDialog.userDetails[0]) || null,
});

export default connect(mapStateToProps)(withStyles(styles)(WebSourceSettings));